import { useNavigate, useParams } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  ProjectTasksDataTableDocument,
  ProjectTasksDataTableQuery,
} from 'generated/resources/datatable.graphql'
import { ProjectTaskAnalyticsDocument } from 'generated/resources/graphql'

import { useDataTable } from 'modules/core/components/DataTable'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import CompleteTask from 'modules/tasks/components/TaskStatus/CompleteTask'
import { TaskIdentifier } from 'modules/tasks/lib/task'

import { ActionType, ActionTypeKey } from './actionTypes'

export const TaskSingleActions = ({
  fromKanban,
  onClickCreateTask,
  segmentedControl,
}: {
  fromKanban?: boolean
  onClickCreateTask: () => void
  segmentedControl?: React.ReactNode
}) => {
  const goToCreateProjectTask = () => {
    track(trackingEvents.projectManagement.tasks.createProjectTaskButton, {
      origin: fromKanban ? 'kanban' : 'datatable',
    })
    onClickCreateTask()
  }

  return (
    <>
      {segmentedControl}
      <Button onClick={goToCreateProjectTask} type="primary" iconName="add">
        {i18n.t('project_management.tasks.add_button')}
      </Button>
      {fromKanban && (
        <Box paddingLeft="s8">
          <Button onClick={() => {}} disabled type="secondary" iconName="ellipsis"></Button>
        </Box>
      )}
    </>
  )
}

const TaskBulkActions = () => {
  const params = useParams()
  const { selectedRows, data } = useDataTable<ProjectTasksDataTableQuery>()
  const selectedTasks = [...selectedRows]
    .map((v) => data.find((t) => t.id === Number(v))?.task?.id)
    .filter((v) => v)
  const refetchQueries = [
    {
      query: ProjectTasksDataTableDocument,
      variables: { ids: [...selectedRows].map((v) => Number(v)), projectIds: [Number(params.id)] },
    },
    {
      query: ProjectTaskAnalyticsDocument,
      variables: { projectIds: [Number(params.id)], completed: true, overdue: true },
    },
  ]

  return (
    <CompleteTask
      identifier={TaskIdentifier.Project}
      taskIds={selectedTasks}
      markAsDone
      disabled={!selectedTasks.length}
      refetchQueries={refetchQueries}
    />
  )
}

export const PopoverActions = ({
  projectId,
  setActionType,
}: {
  projectId: number
  setActionType: (actionType: ActionTypeKey | null) => void
}) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { selectedRows } = useDataTable<ProjectTasksDataTableQuery>()

  if (!selectedRows.size) return null

  return (
    <>
      <PopoverMenu.TouchableOption
        title={i18n.t('project_management.tasks.bulk_actions.duplicate.label')}
        disabled={!selectedRows.size}
        onClick={() => setActionType(ActionType.Duplicate)}
      />

      {selectedRows.size === 1 && (
        <PopoverMenu.TouchableOption
          title={i18n.t('project_management.tasks.bulk_actions.edit.label')}
          onClick={() => {
            const [taskId] = selectedRows

            navigate(
              navigation.projects.id.details.tasks.taskId.fullPath({
                id: projectId,
                taskId: Number(taskId),
              })
            )
          }}
        />
      )}

      <PopoverMenu.TouchableOption
        title={i18n.t('common.delete')}
        danger
        disabled={!selectedRows.size}
        onClick={() => setActionType(ActionType.Delete)}
      />
    </>
  )
}

const Actions = ({
  segmentedControl,
  onClickCreateTask,
}: {
  segmentedControl?: React.ReactNode
  onClickCreateTask: () => void
}) => {
  return (
    <Box flexDirection="row" gap="s8" alignItems="center">
      <TaskSingleActions
        segmentedControl={segmentedControl}
        onClickCreateTask={onClickCreateTask}
      />
      <TaskBulkActions />
    </Box>
  )
}

export default Actions
